/**
 * RRULE expansion.
 *
 * Supports the subset of RFC 5545 that the app actually produces:
 *   FREQ=DAILY|WEEKLY|MONTHLY|YEARLY, INTERVAL, BYDAY, BYMONTHDAY, COUNT, UNTIL
 *
 * Individual occurrences removed by the user are passed in `excluded`.
 */

export interface RRuleParts {
  freq: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  interval: number;
  byDay: number[] | null;
  byMonthDay: number | null;
  count: number | null;
  until: number | null;
}

const DAY_CODES: Record<string, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

export function parseRRule(rrule: string | null | undefined): RRuleParts | null {
  if (!rrule) return null;
  const body = rrule.startsWith("RRULE:") ? rrule.slice(6) : rrule;
  const map = new Map<string, string>();
  for (const part of body.split(";")) {
    const [key, value] = part.split("=");
    if (key && value) map.set(key.trim().toUpperCase(), value.trim());
  }

  const freqRaw = (map.get("FREQ") ?? "").toUpperCase();
  if (
    freqRaw !== "DAILY" &&
    freqRaw !== "WEEKLY" &&
    freqRaw !== "MONTHLY" &&
    freqRaw !== "YEARLY"
  ) {
    return null;
  }

  let byDay: number[] | null = null;
  const byDayRaw = map.get("BYDAY");
  if (byDayRaw) {
    byDay = byDayRaw
      .split(",")
      .map((code) => DAY_CODES[code.toUpperCase()])
      .filter((d): d is number => d !== undefined);
    if (byDay.length === 0) byDay = null;
  }

  const byMonthDayRaw = map.get("BYMONTHDAY");
  const byMonthDay = byMonthDayRaw ? Number(byMonthDayRaw) : null;

  const countRaw = map.get("COUNT");
  const untilRaw = map.get("UNTIL");

  let until: number | null = null;
  if (untilRaw) {
    until = parseUntil(untilRaw);
  }

  return {
    freq: freqRaw,
    interval: Math.max(1, Number(map.get("INTERVAL") ?? 1) || 1),
    byDay,
    byMonthDay:
      byMonthDay && Number.isFinite(byMonthDay) ? byMonthDay : null,
    count: countRaw ? Number(countRaw) : null,
    until,
  };
}

/**
 * Parse an UNTIL value.
 *
 * RFC 5545 uses the compact form `20261008T000000Z` or the date only form
 * `20261008`. Both are UTC.
 */
function parseUntil(raw: string): number | null {
  const match =
    /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2}))?Z?$/.exec(raw.trim());
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour ?? 0),
      Number(minute ?? 0),
      Number(second ?? 0),
    );
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export interface Occurrence {
  start: number;
  end: number;
}

export interface ExpandOptions {
  rrule: string | null | undefined;
  start: number;
  end: number;
  until?: number | null;
  excluded?: number[];
  rangeStart: number;
  rangeEnd: number;
  maxOccurrences?: number;
}

const MAX_ITERATIONS = 2000;

/**
 * Expand an event into the concrete occurrences that fall into
 * `[rangeStart, rangeEnd)`. A non recurring event yields at most its single
 * occurrence.
 */
export function expandOccurrences(options: ExpandOptions): Occurrence[] {
  const {
    rrule,
    start,
    end,
    until = null,
    excluded = [],
    rangeStart,
    rangeEnd,
    maxOccurrences = 400,
  } = options;

  const excludedSet = new Set(excluded);
  const duration = Math.max(0, end - start);
  const parts = parseRRule(rrule);

  const emit = (occStart: number): Occurrence | null => {
    if (excludedSet.has(occStart)) return null;
    return { start: occStart, end: occStart + duration };
  };

  if (!parts) {
    if (start >= rangeStart && start < rangeEnd) {
      const single = emit(start);
      return single ? [single] : [];
    }
    return [];
  }

  // Two independent limits: the UNTIL inside the RRULE and the event's own
  // "repeat until" date. The series ends at whichever comes first.
  const hardLimit = Math.min(
    parts.until ?? Number.POSITIVE_INFINITY,
    until ?? Number.POSITIVE_INFINITY,
  );
  const results: Occurrence[] = [];
  let emitted = 0;

  const push = (occStart: number): boolean => {
    if (occStart > hardLimit) return false;
    if (occStart >= rangeStart && occStart < rangeEnd) {
      const occurrence = emit(occStart);
      if (occurrence) {
        results.push(occurrence);
        emitted++;
      }
    }
    return emitted < maxOccurrences;
  };

  const base = new Date(start);

  if (parts.freq === "DAILY") {
    const step = parts.interval * 86_400_000;
    let cursor = start;
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      if (!push(cursor)) break;
      if (parts.count && emitted >= parts.count) break;
      cursor += step;
      if (cursor > hardLimit && hardLimit !== Number.POSITIVE_INFINITY) break;
      if (cursor > rangeEnd + 366 * 86_400_000) break;
    }
    return results;
  }

  if (parts.freq === "WEEKLY") {
    const days = parts.byDay ?? [base.getDay()];
    // Monday-first week start.
    const weekStart = new Date(base);
    weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
    weekStart.setHours(0, 0, 0, 0);

    const timeOfDay = start - startOfDayMs(base);
    let week = 0;
    for (; week < MAX_ITERATIONS; week++) {
      const weekBase = weekStart.getTime() + week * parts.interval * 7 * 86_400_000;
      let done = false;
      for (const day of [...days].sort((a, b) => a - b)) {
        // `day` is a getDay-style index (0 = Sunday) while the week starts on
        // Monday, so the offset has to be rotated.
        const offset = (day + 6) % 7;
        const occStart = weekBase + offset * 86_400_000 + timeOfDay;
        if (occStart < start) continue;
        if (!push(occStart)) {
          done = true;
          break;
        }
        if (parts.count && emitted >= parts.count) {
          done = true;
          break;
        }
      }
      if (done) break;
      if (weekBase > rangeEnd + 366 * 86_400_000) break;
    }
    return results;
  }

  if (parts.freq === "MONTHLY") {
    const dayOfMonth = parts.byMonthDay ?? base.getDate();
    const timeOfDay = start - startOfDayMs(base);
    let index = 0;
    for (; index < MAX_ITERATIONS; index++) {
      const monthCursor = new Date(
        base.getFullYear(),
        base.getMonth() + index * parts.interval,
        1,
      );
      const daysInThis = new Date(
        monthCursor.getFullYear(),
        monthCursor.getMonth() + 1,
        0,
      ).getDate();
      const day = Math.min(dayOfMonth, daysInThis);
      const occStart = new Date(
        monthCursor.getFullYear(),
        monthCursor.getMonth(),
        day,
      ).getTime() + timeOfDay;
      if (occStart < start) continue;
      if (!push(occStart)) break;
      if (parts.count && emitted >= parts.count) break;
      if (monthCursor.getTime() > rangeEnd + 366 * 86_400_000) break;
    }
    return results;
  }

  // YEARLY
  const month = base.getMonth();
  const dayOfMonth = parts.byMonthDay ?? base.getDate();
  const timeOfDay = start - startOfDayMs(base);
  for (let index = 0; index < MAX_ITERATIONS; index++) {
    const year = base.getFullYear() + index * parts.interval;
    const daysInThis = new Date(year, month + 1, 0).getDate();
    const day = Math.min(dayOfMonth, daysInThis);
    const occStart = new Date(year, month, day).getTime() + timeOfDay;
    if (occStart < start) continue;
    if (!push(occStart)) break;
    if (parts.count && emitted >= parts.count) break;
    if (occStart > rangeEnd + 366 * 86_400_000) break;
  }
  return results;
}

function startOfDayMs(d: Date): number {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

/** Human readable summary used in the UI. */
export function describeRRule(rrule: string | null | undefined): string | null {
  const parts = parseRRule(rrule);
  if (!parts) return null;
  switch (parts.freq) {
    case "DAILY":
      return parts.interval > 1 ? `Mỗi ${parts.interval} ngày` : "Hằng ngày";
    case "WEEKLY":
      return parts.interval > 1
        ? `Mỗi ${parts.interval} tuần`
        : "Hằng tuần";
    case "MONTHLY":
      return parts.interval > 1
        ? `Mỗi ${parts.interval} tháng`
        : "Hằng tháng";
    case "YEARLY":
      return parts.interval > 1 ? `Mỗi ${parts.interval} năm` : "Hằng năm";
  }
}
